import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

import { useAppContext } from '../AppContext';
import CreateBountyModal from './CreateBountyModal';
import LoadingSpinner from './LoadingSpinner';
import AuraProgressBar from './AuraProgressBar';
import SpiritGuideAvatar from './SpiritGuideAvatar';
import Celebration from './Celebration';
import QuestPathVisualizer from './QuestPathVisualizer';
import QuestXPBar from './QuestXPBar';
import QuestCompassHint from './QuestCompassHint';
import QuestJournal from './QuestJournal';

function QuestBoard() {
    const { t } = useTranslation();
    const { userProfiles, userId, appFunctions, setMessage, db, appId } = useAppContext();
    const [categorizedQuests, setCategorizedQuests] = useState({ daily: [], weekly: [], monthly: [], onboarding: [], milestones: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [claimingId, setClaimingId] = useState(null);
    const [showCelebration, setShowCelebration] = useState(false);
    const [optimisticallyClaimed, setOptimisticallyClaimed] = useState(new Set());
    const [filterType, setFilterType] = useState('all');
    const [sortBy, setSortBy] = useState('default');
    const [activeTab, setActiveTab] = useState('active');

    const [bounties, setBounties] = useState([]);
    const [showCreateBountyModal, setShowCreateBountyModal] = useState(false);

    useEffect(() => {
        if (!db || !appId) return;
        const bountiesRef = collection(db, `artifacts/${appId}/public/data/bounties`);
        const q = query(bountiesRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedBounties = [];
            snapshot.forEach(doc => {
                fetchedBounties.push({ id: doc.id, ...doc.data() });
            });
            setBounties(fetchedBounties);
        });
        return () => unsubscribe();
    }, [db, appId]);

    const currentUserProfile = userProfiles.find(p => p.id === userId);

    const allQuests = useMemo(() => [
        // --- ONBOARDING: Guiding users to core, sticky features ---
        { id: 'onboarding_post_whisper', title: t('quests.onboarding.postWhisper.title'), description: t('quests.onboarding.postWhisper.description'), reward: 25, type: 'onboarding', canClaim: p => (p.stats?.totalWhispers || 0) >= 1, currentProgress: p => p.stats?.totalWhispers || 0, targetProgress: 1 },
        { id: 'onboarding_profile_bio', title: t('quests.onboarding.profileBio.title'), description: t('quests.onboarding.profileBio.description'), reward: 20, type: 'onboarding', canClaim: p => p.bio && p.bio.length > 10, currentProgress: p => (p.bio && p.bio.length > 10) ? 1 : 0, targetProgress: 1 },
        { id: 'onboarding_join_nexus', title: t('quests.onboarding.joinNexus.title'), description: t('quests.onboarding.joinNexus.description'), reward: 30, type: 'onboarding', canClaim: p => p.nexusMemberships && p.nexusMemberships.length > 0, currentProgress: p => (p.nexusMemberships && p.nexusMemberships.length > 0) ? 1 : 0, targetProgress: 1 },
        { id: 'onboarding_open_chamber', title: t('quests.onboarding.openChamber.title'), description: t('quests.onboarding.openChamber.description'), reward: 10, type: 'onboarding', canClaim: p => !!p.lastChamberOpen, currentProgress: p => p.lastChamberOpen ? 1 : 0, targetProgress: 1 },
        { id: 'onboarding_make_connection', title: t('quests.onboarding.makeConnection.title'), description: t('quests.onboarding.makeConnection.description'), reward: 15, type: 'onboarding', canClaim: p => (p.stats?.totalConnections || 0) >= 1, currentProgress: p => p.stats?.totalConnections || 0, targetProgress: 1 },

        // --- DAILY: Building retention and daily habits ---
        { id: 'daily_post_whisper', title: t('quests.daily.postWhisper.title'), description: t('quests.daily.postWhisper.description'), reward: 15, type: 'daily', canClaim: p => (p.dailyQuestProgress?.posts || 0) >= 1, currentProgress: p => p.dailyQuestProgress?.posts || 0, targetProgress: 1 },
        { id: 'daily_nexus_comment', title: t('quests.daily.nexusComment.title'), description: t('quests.daily.nexusComment.description'), reward: 20, type: 'daily', canClaim: p => (p.dailyQuestProgress?.nexusComments || 0) >= 2, currentProgress: p => p.dailyQuestProgress?.nexusComments || 0, targetProgress: 2 },
        { id: 'daily_open_chamber', title: t('quests.daily.openChamber.title'), description: t('quests.daily.openChamber.description'), reward: 10, type: 'daily', canClaim: p => (p.dailyQuestProgress?.echoChambersOpened || 0) >= 1, currentProgress: p => p.dailyQuestProgress?.echoChambersOpened || 0, targetProgress: 1 },

        // --- WEEKLY: Driving deeper engagement and content quality ---
        { id: 'weekly_quality_post', title: t('quests.weekly.qualityPost.title'), description: t('quests.weekly.qualityPost.description'), reward: 75, type: 'weekly', canClaim: p => (p.weeklyQuestProgress?.qualityPosts || 0) >= 1, currentProgress: p => p.weeklyQuestProgress?.qualityPosts || 0, targetProgress: 1 },
        { id: 'weekly_social_butterfly', title: t('quests.weekly.socialButterfly.title'), description: t('quests.weekly.socialButterfly.description'), reward: 60, type: 'weekly', canClaim: p => (p.weeklyQuestProgress?.uniqueConversations || 0) >= 3, currentProgress: p => p.weeklyQuestProgress?.uniqueConversations || 0, targetProgress: 3 },
        { id: 'weekly_nexus_champion', title: t('quests.weekly.nexusChampion.title'), description: t('quests.weekly.nexusChampion.description'), reward: 100, type: 'weekly', canClaim: p => (p.weeklyQuestProgress?.nexusChampionDays || 0) >= 1, currentProgress: p => p.weeklyQuestProgress?.nexusChampionDays || 0, targetProgress: 1 },
        { id: 'weekly_patron', title: t('quests.weekly.patron.title'), description: t('quests.weekly.patron.description'), reward: 50, type: 'weekly', canClaim: p => (p.weeklyQuestProgress?.echoesGifted || 0) >= 50, currentProgress: p => p.weeklyQuestProgress?.echoesGifted || 0, targetProgress: 50 },

        // --- MILESTONES: Rewarding long-term investment and platform loyalty ---
        { id: 'milestone_rep_100', title: t('quests.milestones.rep100.title'), description: t('quests.milestones.rep100.description'), reward: 50, type: 'milestone', canClaim: p => (p.reputationScore || 0) >= 100, currentProgress: p => p.reputationScore || 0, targetProgress: 100 },
        { id: 'milestone_rep_500', title: t('quests.milestones.rep500.title'), description: t('quests.milestones.rep500.description'), reward: 150, type: 'milestone', canClaim: p => (p.reputationScore || 0) >= 500, currentProgress: p => p.reputationScore || 0, targetProgress: 500 },
        { id: 'milestone_create_nexus', title: t('quests.milestones.createNexus.title'), description: t('quests.milestones.createNexus.description'), reward: 250, type: 'milestone', canClaim: p => (p.stats?.nexusesCreated || 0) >= 1, currentProgress: p => p.stats?.nexusesCreated || 0, targetProgress: 1 },
        { id: 'milestone_login_streak_30', title: t('quests.milestones.loginStreak30.title'), description: t('quests.milestones.loginStreak30.description'), reward: 500, type: 'milestone', canClaim: p => (p.stats?.loginStreak || 0) >= 30, currentProgress: p => p.stats?.loginStreak || 0, targetProgress: 30 },
    ], [t]);

    useEffect(() => {
        if (currentUserProfile) {
            const categorized = { daily: [], weekly: [], monthly: [], onboarding: [], milestones: [] };
            allQuests.forEach(q => {
                if (categorized[q.type]) categorized[q.type].push(q);
            });
            setCategorizedQuests(categorized);
            setIsLoading(false);
        }
    }, [currentUserProfile, allQuests]);

    const handleClaimReward = useCallback(async (quest) => { // Expect the full quest object
        setClaimingId(quest.id);
        
        setOptimisticallyClaimed(prev => new Set(prev).add(quest.id));

        const claimQuestReward = httpsCallable(appFunctions, 'claimQuestReward');
        try {
            const result = await claimQuestReward({ quest }); // Pass the entire quest object
            setMessage(t('quests.claimSuccess', { title: quest.title, reward: result.data.reward }), 'success');
            setShowCelebration(true);
        } catch (error) {
            setMessage(t('quests.claimError', { message: error.message }));
            setOptimisticallyClaimed(prev => {
                const newSet = new Set(prev);
                newSet.delete(quest.id);
                return newSet;
            });
        } finally {
            setClaimingId(null);
        }
    }, [appFunctions, setMessage, t]);

    if (isLoading || !currentUserProfile) {
        return <LoadingSpinner message={t('quests.loading')} />;
    }

    const QuestCard = ({ quest }) => {
        const [timeLeft, setTimeLeft] = useState('');
        const completionTimestamp = currentUserProfile.completedQuests?.[quest.id];
        const isPermanentlyCompleted = (quest.type === 'onboarding' || quest.type === 'milestone') && !!completionTimestamp;
        const isOptimisticallyClaimed = optimisticallyClaimed.has(quest.id);

        const actualCurrentProgress = typeof quest.currentProgress === 'function' ? quest.currentProgress(currentUserProfile) : quest.currentProgress;
        const actualTargetProgress = typeof quest.targetProgress === 'function' ? quest.targetProgress(currentUserProfile) : quest.targetProgress;

        const isOnCooldown = useMemo(() => {
            if (!completionTimestamp || isPermanentlyCompleted) return false;
            
            const getCooldown = () => {
                if (quest.type === 'daily') return 22 * 3600 * 1000;
                if (quest.type === 'weekly') return 6.5 * 24 * 3600 * 1000;
                return 0;
            };
            const cooldown = getCooldown();
            return (Date.now() - completionTimestamp.toDate().getTime()) < cooldown;
        }, [completionTimestamp, isPermanentlyCompleted, quest.type]);

        useEffect(() => {
            if (isOnCooldown) {
                const interval = setInterval(() => {
                    const getCooldown = () => {
                        if (quest.type === 'daily') return 22 * 3600 * 1000;
                        if (quest.type === 'weekly') return 6.5 * 24 * 3600 * 1000;
                        return 0;
                    };
                    const cooldown = getCooldown();
                    const cooldownEndTime = completionTimestamp.toDate().getTime() + cooldown;
                    const timeLeftMs = cooldownEndTime - Date.now();

                    if (timeLeftMs <= 0) {
                        setTimeLeft('');
                        clearInterval(interval);
                    } else {
                        const hours = Math.floor(timeLeftMs / (1000 * 60 * 60));
                        const minutes = Math.floor((timeLeftMs % (1000 * 60 * 60)) / (1000 * 60));
                        const seconds = Math.floor((timeLeftMs % (1000 * 60)) / 1000);
                        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
                    }
                }, 1000);
                return () => clearInterval(interval);
            }
        }, [isOnCooldown, completionTimestamp, quest.type]);

        const meetsClaimConditions = quest.canClaim(currentUserProfile);
        const isClaimable = meetsClaimConditions && !isOnCooldown && !isPermanentlyCompleted && !isOptimisticallyClaimed;

        const getButtonState = () => {
            if (claimingId === quest.id) {
                return { text: <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>, disabled: true };
            }
            if (isOptimisticallyClaimed || isOnCooldown) {
                return { text: timeLeft || t('quests.locked'), disabled: true };
            }
            if (isClaimable) {
                return { text: t('quests.claim'), disabled: false };
            }
            return { text: t('quests.inProgress'), disabled: true };
        };

        const buttonState = getButtonState();

        return (
            <div className={`relative p-4 rounded-xl flex items-start space-x-4 transition-all duration-300 bg-white/5 border border-white/10 hover:bg-white/10 ${isPermanentlyCompleted || isOptimisticallyClaimed ? 'opacity-60' : ''}`}>
                {(isPermanentlyCompleted || isOptimisticallyClaimed) && <div className="absolute inset-0 bg-black/40 rounded-xl backdrop-blur-[2px]"></div>}
                <div className="flex-grow">
                    <h4 className="font-bold text-white">{quest.title}</h4>
                    <p className="text-sm text-gray-300 mt-1">{quest.description}</p>
                    {actualTargetProgress > 0 && (
                        <AuraProgressBar 
                            current={Math.min(actualCurrentProgress, actualTargetProgress)} 
                            target={actualTargetProgress} 
                            label={`${Math.min(actualCurrentProgress, actualTargetProgress)}/${actualTargetProgress}`} 
                        />
                    )}
                    <div className="flex items-center text-xs font-bold text-yellow-400 mt-2">
                        <LucideIcons.Gem size={16} className="mr-1 text-yellow-300" />
                        {t('quests.rewardLabel')}: {quest.reward} {t('quests.echoes')}
                    </div>
                </div>
                {!isPermanentlyCompleted && (
                    <button onClick={() => handleClaimReward(quest)} disabled={buttonState.disabled} className={`self-center px-4 py-1 text-white font-bold rounded-full transition duration-300 w-28 text-center text-sm h-8 ${isClaimable ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 cursor-not-allowed'}`}>
                        {buttonState.text}
                    </button>
                )}
                {isPermanentlyCompleted && (
                    <div className="self-center flex items-center justify-center px-4 py-1 text-green-300 font-bold rounded-full bg-green-900/50 w-28 text-center text-sm h-8">
                        <LucideIcons.CheckCircle size={16} className="mr-2" /> {t('quests.claimed')}
                    </div>
                )}
            </div>
        );
    };

    const QuestCategory = ({ title, questList }) => {
        if (!questList || questList.length === 0) return null;
        const filteredList = questList.filter(q => {
            if (filterType === 'all') return true;
            return q.type === filterType;
        });

        if (filteredList.length === 0) return null;

        return (
            <div>
                <h3 className="text-xl font-bold mb-4 text-blue-200 font-sans">{title}</h3>
                <div className="space-y-3">
                    {filteredList.map(q => <QuestCard key={q.id} quest={q} />)}
                </div>
            </div>
        );
    };

    return (
        <div className="animate-fadeIn space-y-8">
            {showCelebration && <Celebration onComplete={() => setShowCelebration(false)} />}
            <div className="flex justify-center border-b border-gray-700 mb-6">
                <button onClick={() => setActiveTab('active')} className={`profile-tab-button ${activeTab === 'active' ? 'active' : ''}`}>{t('quests.tabs.active')}</button>
                <button onClick={() => setActiveTab('bounties')} className={`profile-tab-button ${activeTab === 'bounties' ? 'active' : ''}`}>Community Bounties</button>
                <button onClick={() => setActiveTab('journal')} className={`profile-tab-button ${activeTab === 'journal' ? 'active' : ''}`}>{t('quests.tabs.journal')}</button>
            </div>

            {activeTab === 'active' && (
                <>
                    <SpiritGuideAvatar message={t('quests.spiritGuideWelcome')} />
                    <QuestXPBar 
                        currentXP={currentUserProfile?.questXP || 0} 
                        nextLevelXP={currentUserProfile?.nextQuestLevelXP || 100} 
                        level={currentUserProfile?.questLevel || 1} 
                    />
                    
                    {/* Gamification: Streak Multiplier Display */}
                    <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-500/30 rounded-xl p-4 flex items-center justify-between shadow-lg">
                        <div className="flex items-center space-x-4">
                            <div className="bg-orange-500/20 p-3 rounded-full text-orange-400">
                                <LucideIcons.Flame size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-white text-lg">Social Momentum Streak</h4>
                                <p className="text-sm text-gray-300">{(currentUserProfile?.stats?.loginStreak || 0)} Days of continuous positive interactions</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-400">
                                {Math.min(2.5, 1 + ((currentUserProfile?.stats?.loginStreak || 0) * 0.1)).toFixed(1)}x
                            </div>
                            <div className="text-xs text-gray-400">Echo Multiplier</div>
                        </div>
                    </div>
                    <QuestCategory title={t('quests.categories.onboarding')} questList={categorizedQuests.onboarding} />
                    <QuestCategory title={t('quests.categories.daily')} questList={categorizedQuests.daily} />
                    <QuestCategory title={t('quests.categories.weekly')} questList={categorizedQuests.weekly} />
                    <QuestCategory title={t('quests.categories.monthly')} questList={categorizedQuests.monthly} />
                    <QuestCategory title={t('quests.categories.milestones')} questList={categorizedQuests.milestones} />
                </>
            )}

            {activeTab === 'journal' && <QuestJournal />}

            {/* Bounties Tab */}
            {activeTab === 'bounties' && (
                <div className="space-y-6 animate-fadeIn">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-2xl font-bold text-blue-100 font-sans">Active Bounties</h3>
                        <button onClick={() => setShowCreateBountyModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full font-bold shadow-lg transition duration-300 text-sm flex items-center">
                            <LucideIcons.Plus size={16} className="mr-2" /> Post Bounty
                        </button>
                    </div>
                    <p className="text-gray-300 text-sm mb-6">Attach Echoes to your whispers to drive high-quality community responses. Rewards are funded by users.</p>
                    
                    <div className="space-y-4">
                        {bounties.length === 0 && (
                            <p className="text-center text-gray-400 py-8">No active bounties at the moment.</p>
                        )}
                        {bounties.map(bounty => (
                            <div key={bounty.id} className="relative p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all shadow-md group">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 text-xs text-gray-400 mb-1">
                                            <LucideIcons.User size={12} />
                                            <span>{bounty.creatorName || 'Anonymous'}</span>
                                            <span>•</span>
                                            <LucideIcons.Clock size={12} />
                                            <span>Active</span>
                                        </div>
                                        <h4 className="font-bold text-white text-lg mb-1">{bounty.title}</h4>
                                        <p className="text-sm text-gray-300">{bounty.description}</p>
                                    </div>
                                    <div className="ml-4 flex flex-col items-center justify-center bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-xl min-w-[80px]">
                                        <LucideIcons.Gem size={20} className="text-yellow-400 mb-1" />
                                        <span className="font-bold text-yellow-400">{bounty.reward}</span>
                                    </div>
                                </div>
                                <div className="mt-4 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg transition">
                                        Submit Entry
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {showCreateBountyModal && (
                <CreateBountyModal onClose={() => setShowCreateBountyModal(false)} />
            )}
        </div>
    );
}

export default QuestBoard;
